import {Search} from 'lucide-react';
import {PhoneHQButton, PhoneHQCheckbox, PhoneHQInput, PhoneHQLoader, PhoneHQConfirmDialog} from 'ch3-ui-lib';
import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {showToast} from "../../../store/features/Notifications/notificationsSlice";
import {
    GoogleImportStatusResponse,
    GoogleUser,
    useDeleteGoogleIntegrationMutation,
    useGetCurrentGoogleIntegrationQuery,
    useGetCurrentGoogleIntegrationUsersQuery,
    useImportGoogleContactsMutation,
    useStatusGoogleIntegrationMutation,
} from "../../../store/features/integrations/integrationsService";
import {useAppDispatch} from "../../../store/store";
import useMediaQuery from "../../../utilis/screenWidthUtils";
import {getActiveGoogleIntegration} from "../Google/googleIntegrationState";
import {getGoogleIntegrationErrorMessage} from "../Google/googleIntegrationErrors";
import {UserRow} from "./UserRow";

const POLLING_INTERVAL_MS = 5000;
const LOAD_USERS_ERROR_MESSAGE =
    'We could not load Google Workspace users. Try again, or reconnect the integration if the problem continues.';
const START_IMPORT_ERROR_MESSAGE =
    'We could not start the user sync. Try again, or contact PhoneHQ Support if the problem continues.';
const STATUS_ERROR_MESSAGE =
    'We could not refresh the user sync status. The sync may still be running; refresh the page to check again.';
const IMPORT_FAILED_MESSAGE =
    'The user sync stopped before completion. Try again, or contact PhoneHQ Support if the problem continues.';
const DISCONNECT_ERROR_MESSAGE =
    'We could not disconnect Google Workspace. Try again, or contact PhoneHQ Support if the problem continues.';

export const UserList = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery('(max-width: 960px)');

    const {
        data: integrationData,
        error: integrationError,
        isLoading: isIntegrationLoading,
        isFetching: isIntegrationFetching,
        isError: isIntegrationError,
        refetch: refetchIntegration,
    } = useGetCurrentGoogleIntegrationQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const integration = isIntegrationFetching ? undefined : getActiveGoogleIntegration(integrationData);
    const integrationId = integration?.id;

    const {
        data: usersData,
        error: usersError,
        isLoading: isUsersLoading,
        isError: isUsersError,
        refetch: refetchUsers,
    } = useGetCurrentGoogleIntegrationUsersQuery(
        {integrationId: integrationId ?? 0},
        {
            skip: integrationId === undefined,
            refetchOnMountOrArgChange: false,
            refetchOnFocus: false,
            refetchOnReconnect: false,
        },
    );

    const [disconnectIntegrationMutation] = useDeleteGoogleIntegrationMutation();
    const [startImportMutation] = useImportGoogleContactsMutation();
    const [getStatusMutation] = useStatusGoogleIntegrationMutation();
    const [disconnectOpen, setDisconnectOpen] = useState(false);
    const [userList, setUserList] = useState<GoogleUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<GoogleUser[]>([]);
    const [importId, setImportId] = useState<number | null>(null);
    const [importStatus, setImportStatus] = useState<GoogleImportStatusResponse | null>(null);
    const [isImportStarting, setIsImportStarting] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [showImportResults, setShowImportResults] = useState(false);
    const [pollingErrorMessage, setPollingErrorMessage] = useState<string | null>(null);
    const pollingRequestInProgress = useRef(false);

    useEffect(() => {
        if (!usersData || showImportResults) return;
        setUserList(usersData.googleResponse.users);
        setSelectedUsers([]);
    }, [showImportResults, usersData]);

    useEffect(() => {
        if (!isPolling || importId === null || integrationId === undefined) return;

        let active = true;
        const pollStatus = async () => {
            if (pollingRequestInProgress.current) return;
            pollingRequestInProgress.current = true;

            try {
                const status = await getStatusMutation({
                    integrationId,
                    importId,
                    emails: [],
                }).unwrap();
                if (!active) return;

                setPollingErrorMessage(null);
                setImportStatus(status);
                if (status.status === 'FINISHED' || status.status === 'FAILED') {
                    setIsPolling(false);
                    setShowImportResults(true);
                    void refetchUsers();
                }
                if (status.status === 'FAILED') {
                    dispatch(showToast({
                        title: 'User sync failed',
                        description: IMPORT_FAILED_MESSAGE,
                        type: 'error',
                    }));
                }
            } catch (error: unknown) {
                if (!active) return;
                setIsPolling(false);
                const message = getGoogleIntegrationErrorMessage(error, STATUS_ERROR_MESSAGE);
                setPollingErrorMessage(message);
                dispatch(showToast({
                    title: 'Could not refresh user sync',
                    description: message,
                    type: 'error',
                }));
            } finally {
                pollingRequestInProgress.current = false;
            }
        };

        void pollStatus();
        const pollingInterval = window.setInterval(() => void pollStatus(), POLLING_INTERVAL_MS);
        return () => {
            active = false;
            window.clearInterval(pollingInterval);
        };
    }, [dispatch, getStatusMutation, importId, integrationId, isPolling, refetchUsers]);

    const handleSelect = (user: GoogleUser) => {
        setSelectedUsers((current) => current.some(({email}) => email === user.email)
            ? current.filter(({email}) => email !== user.email)
            : [...current, user]);
    };

    const handleSelectAll = () => {
        setSelectedUsers((current) => current.length === userList.length ? [] : userList);
    };

    const selectedAllState = (): boolean | 'indeterminate' => {
        if (selectedUsers.length === 0) return false;
        return selectedUsers.length === userList.length ? true : 'indeterminate';
    };

    const startImport = async () => {
        if (integrationId === undefined) {
            dispatch(showToast({description: 'Reconnect Google Workspace before syncing users.', type: 'error'}));
            return;
        }
        if (selectedUsers.length === 0) {
            dispatch(showToast({description: 'Select at least one user to sync.', type: 'error'}));
            return;
        }

        setIsImportStarting(true);
        setImportStatus(null);
        setPollingErrorMessage(null);
        try {
            const response = await startImportMutation({
                integration: integrationId,
                users: selectedUsers,
            }).unwrap();
            setImportId(response.importId);
            setUserList(selectedUsers);
            setShowImportResults(true);
            setIsPolling(true);
        } catch (error: unknown) {
            setIsPolling(false);
            setShowImportResults(false);
            dispatch(showToast({
                title: 'Could not start user sync',
                description: getGoogleIntegrationErrorMessage(error, START_IMPORT_ERROR_MESSAGE),
                type: 'error',
            }));
        } finally {
            setIsImportStarting(false);
        }
    };

    const disconnectIntegration = async () => {
        if (integrationId === undefined) return;
        try {
            await disconnectIntegrationMutation({integrationId}).unwrap();
            navigate('/admin/integrations/google/disconnected');
        } catch (error: unknown) {
            dispatch(showToast({
                title: 'Could not disconnect Google Workspace',
                description: getGoogleIntegrationErrorMessage(error, DISCONNECT_ERROR_MESSAGE),
                type: 'error',
            }));
        }
    };

    const handleSearch = (value: string) => {
        const source = showImportResults ? selectedUsers : usersData?.googleResponse?.users ?? [];
        const phrase = value.trim().toLowerCase();
        if (!phrase) {
            setUserList(source);
            return;
        }
        setUserList(source.filter((user) =>
            (user.firstName || '').toLowerCase().includes(phrase) ||
            (user.lastName || '').toLowerCase().includes(phrase) ||
            (user.email || '').toLowerCase().includes(phrase),
        ));
    };

    const getExistingUserStatus = (user: GoogleUser) => {
        if (usersData?.alreadyExists?.includes(user.email)) return 'ALREADY_EXISTS' as const;
        if (usersData?.alreadySynced?.includes(user.email)) return 'ALREADY_SYNCED' as const;
        return false;
    };

    const retryLoading = () => {
        void refetchIntegration();
        if (integrationId !== undefined) void refetchUsers();
    };

    if (isIntegrationLoading || isIntegrationFetching || (integrationId !== undefined && isUsersLoading)) {
        return (
            <div className={`flex w-full ${isMobile ? 'px-4' : ''}`}>
                <PhoneHQLoader/>
            </div>
        );
    }

    if (isIntegrationError || isUsersError) {
        const error = usersError ?? integrationError;
        return (
            <div className='mx-auto mt-12 max-w-[580px] border border-danger-ink bg-danger-tint p-6'>
                <h2 className='font-semibold'>Could not load Google Workspace users</h2>
                <p className='my-3 text-sm'>{getGoogleIntegrationErrorMessage(error, LOAD_USERS_ERROR_MESSAGE)}</p>
                <PhoneHQButton onClick={retryLoading} phq-id='integrationGoogleUserList-retryBtn'>Try again</PhoneHQButton>
            </div>
        );
    }

    if (!integration) {
        return (
            <div className='mx-auto mt-12 max-w-[580px] border border-danger-ink bg-danger-tint p-6'>
                <h2 className='font-semibold'>Google Workspace is not connected</h2>
                <p className='my-3 text-sm'>Reconnect Google Workspace before syncing users.</p>
                <PhoneHQButton onClick={() => navigate('/admin/integrations/google/connect')} phq-id='integrationGoogleUserList-reconnectBtn'>Reconnect Google Workspace</PhoneHQButton>
            </div>
        );
    }

    return (
        <>
            <div className='flex justify-end w-full mr-8 relative bottom-[42px]'>
                <PhoneHQInput
                    phq-id='integrationGoogleUserList-searchInput'
                    placeholder='Search'
                    containerClassName={isMobile ? '' : 'min-w-[375px]'}
                    prefixIcon={<Search size={16} aria-hidden="true" className="!p-0"/>}
                    onChange={(e) => handleSearch(e.target.value)}
                />
            </div>
            <div className='w-full'>
                <div className={`flex flex-col border ${isMobile ? 'mt-6' : 'mt-8'} border-line bg-surface`}>
                    <thead className={`border-b border-line`}>
                        <div className={`font-semibold flex items-center text-lg mb-0 py-5 pr-6 pl-6 border-b border-line`}>
                            {showImportResults ? 'Imported users' : 'List of users'}
                        {showImportResults && (
                                <div className='font-normal text-base ml-6'>
                                    Successful
                                    <span className='py-1 font-normal mx-4 text-xs border border-success-ink px-2 text-success-ink'>
                                        {importStatus?.entitiesCreated ?? 0}
                                    </span>
                                    Failed
                                    <span className='py-1 font-normal mx-4 text-xs border border-danger-ink px-2 text-danger-ink'>
                                        {importStatus?.entitiesRejected ?? 0}
                                    </span>
                                </div>
                            )}
                        </div>
                        {pollingErrorMessage && (
                            <div className='ml-auto flex items-center gap-3 text-sm font-normal text-danger-ink'>
                                <span>{pollingErrorMessage}</span>
                                <PhoneHQButton variant="secondary" onClick={() => {
                                        setPollingErrorMessage(null);
                                        setIsPolling(true);
                                    }} phq-id='integrationGoogleUserList-retryStatusBtn'>Try status again</PhoneHQButton>
                            </div>
                        )}
                        {!showImportResults && (
                            <tr className={`border-b border-line`}>
                                <div className='font-semibold p-3'>
                                    <PhoneHQCheckbox
                                        checked={selectedAllState()}
                                        label='Select all'
                                        onCheckedChange={() => handleSelectAll()}
                                    />
                                </div>
                            </tr>
                        )}
                    </thead>
                    <div className='h-[calc(100vh-600px)] overflow-y-auto'>
                        {userList.map((user) => {
                            const result = importStatus?.entitiesStatuses.find(({email}) => email === user.email);
                            return (
                                <UserRow
                                    key={user.email}
                                    isPolling={isPolling}
                                    showImportStatus={showImportResults}
                                    user={user}
                                    userExists={showImportResults ? false : getExistingUserStatus(user)}
                                    userStatus={result?.status}
                                    userMessage={result?.message}
                                    handleSelect={handleSelect}
                                    isSelected={selectedUsers.some(({email}) => email === user.email)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className='flex justify-between w-full py-4 pr-4'>
                {!integration.details.disabled && (
                    <div className='flex'>
                        <div className='bg-surface-muted flex items-center text-sm px-2 gap-2'>
                            <span className='text-xs'>Google account</span>
                            <span className='font-semibold'>{integration.details.userEmail}</span>
                        </div>
                        <PhoneHQButton variant="text-danger" onClick={() => setDisconnectOpen(true)} phq-id='integrationGoogleUserList-disconnectBtn'>Disconnect</PhoneHQButton>
                        <PhoneHQConfirmDialog
                            open={disconnectOpen}
                            onOpenChange={setDisconnectOpen}
                            title="Disconnect integration?"
                            cancelLabel="Cancel"
                            confirmLabel="Yes, disconnect"
                            tone="danger"
                            onConfirm={() => {
                                setDisconnectOpen(false);
                                void disconnectIntegration();
                            }}
                        >
                            By disconnecting this integration, you will lose all the data and this action cannot be undone.
                        </PhoneHQConfirmDialog>
                    </div>
                )}
                {!showImportResults ? (
                    <PhoneHQButton loading={isImportStarting} onClick={() => void startImport()} className='w-[190px] h-[47px]' phq-id='integrationGoogleUserList-syncUsersBtn'>Sync users</PhoneHQButton>
                ) : (
                    <PhoneHQButton onClick={() => navigate('/admin/integrations')} phq-id='integrationGoogleUserList-goToIntegrationsListBtn'>Done</PhoneHQButton>
                )}
            </div>
        </>
    );
};
